/**
 * contentValidators/notationChecks.ts
 * Validates notation requirements: LaTeX fractions, comma decimals,
 * euro notation, and superscript units.
 */

import type { ValidationIssue } from '../../issueContract';
import { coach } from '../../issueContract';
import { getNotationRequirements, isPolicyFeatureEnabled } from '../../policyEngine';
import type { Question } from '../../../../content';
import { STAGE } from './shared';

/**
 * Validate notation requirements for a question.
 */
export function validateNotation(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const module = question.module;
  if (!module) return issues;
  const notation = getNotationRequirements(module);
  const fullText = (question.question ?? '') + ' ' + (question.options?.join(' ') ?? '');

  // LaTeX fractions
  if (notation.requireLatexFractions && isPolicyFeatureEnabled('enforceLatex')) {
    const isFractionContext = fId.includes('BREUK') || (question.skill ?? '').toLowerCase().includes('breuk');
    if (isFractionContext && fullText.includes('/') && !fullText.includes('\\frac')) {
      issues.push(coach('content', STAGE, 'CONTENT_MISSING_LATEX', 'Fractions should use LaTeX (\\frac{}{}) instead of /', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: 35
      }));
    }
  }

  // Comma decimals
  if (notation.requireCommaDecimals) {
    // Strip Dutch thousand-separator dots before checking for decimal dots.
    const textForDotCheck = fullText.replace(/\d\.\d{3}(?!\d)/g, match => match.replace('.', ''));
    if (textForDotCheck.match(/\d+\.\d+/) && !fId.includes('WETENSCHAPPELIJK')) {
      issues.push(coach('content', STAGE, 'CONTENT_DOT_DECIMAL', 'Use comma (,) for decimals, not dot (.)', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: 40
      }));
    }
  }

  // Euro comma
  if (notation.requireEuroComma) {
    if (fullText.includes('€') && fullText.match(/\d+\.\d{2}/)) {
      issues.push(coach('content', STAGE, 'CONTENT_EURO_DOT', 'Use comma (€3,50) not dot (€3.50) for euro notation', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: 40
      }));
    }
  }

  // Superscript units
  if (notation.requireSuperscriptUnits && isPolicyFeatureEnabled('enforceUnitNotation')) {
    const requiresSuperscript = fId.includes('OPP') || fId.includes('INH');
    if (requiresSuperscript && (fullText.includes('m2') || fullText.includes('cm2') || fullText.includes('m3'))) {
      issues.push(coach('content', STAGE, 'CONTENT_MISSING_SUPERSCRIPT', 'Use superscripts (m², cm³) instead of m2, cm3', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: 35
      }));
    }
  }
  return issues;
}